import { ApolloClient, ApolloLink, createHttpLink, InMemoryCache, split } from "@apollo/client";
import { loadDevMessages, loadErrorMessages } from "@apollo/client/dev";
import { GraphQLWsLink } from "@apollo/client/link/subscriptions";
import { getMainDefinition } from "@apollo/client/utilities";
import { createClient } from "graphql-ws";

import { createAuthLink, createAuthTokenAnnotationLink, createUnauthorizedLink } from "./auth-links";
import cacheConfig from "./generated/cacheConfig";
import { invalidationLink } from "./invalidationLink";
import { timeoutLink } from "./timeoutLink";
import { wsLifecycleEmitter } from "./ws-lifecycle";

// Re-export Apollo Client core functionality for library consumers
export * from "@apollo/client";

loadDevMessages();
loadErrorMessages();

// Configuration interface for creating Apollo Client
export interface ApolloClientConfig {
  httpUri: string;
  wsUri: string;
  getAccessToken: (require?: boolean) => string | undefined;
  onUnauthorized?: () => void;
}

// Factory function to create Apollo Client with configuration
export function createApolloClient(config: ApolloClientConfig): ApolloClient<any> {
  const wsLink = new GraphQLWsLink(
    createClient({
      url: config.wsUri,
      connectionParams: () => {
        const authToken = config.getAccessToken();

        if (!authToken) {
          return {};
        }

        return {
          authorization: `Bearer ${authToken}`,
        };
      },
      // Mirror graphql-ws transport events into a module-level emitter so
      // subscription consumers can react to reconnects. graphql-ws does
      // not replay events published while the socket was disconnected,
      // and Apollo Client doesn't expose the underlying connection state,
      // so consumers that need a reconnect signal (e.g., `use-mcp-chat`'s
      // stream-attach probe) subscribe to `wsLifecycleEmitter` directly.
      on: {
        connecting: () => wsLifecycleEmitter.emit("connecting"),
        connected: () => wsLifecycleEmitter.emit("connected"),
        closed: (event) => wsLifecycleEmitter.emit("closed", event),
        error: (err) => wsLifecycleEmitter.emit("error", err),
      },
    })
  );

  const apiHttpLink = createHttpLink({
    uri: config.httpUri,
    fetch,
  });

  const authLink = createAuthLink(config.getAccessToken);

  const cleanTypeName = new ApolloLink((operation, forward) => {
    if (operation.variables) {
      const omitTypename = (key: string, value: unknown) => (key === "__typename" ? undefined : value);
      operation.variables = JSON.parse(JSON.stringify(operation.variables), omitTypename) as Record<string, unknown>;
    }
    return forward(operation);
  });

  // Build the HTTP link chain, optionally prepending an error link for invalid sessions.
  //
  // The error link goes first so it sits *above* `authLink`, which is what lets it read the token
  // that the failing operation actually sent (`AUTH_TOKEN_CONTEXT_KEY`) and ignore a 401 that
  // belongs to a credential already replaced. See `createUnauthorizedLink`.
  const httpLinks: ApolloLink[] = [];

  if (config.onUnauthorized) {
    httpLinks.push(
      createUnauthorizedLink({ getAccessToken: config.getAccessToken, onUnauthorized: config.onUnauthorized })
    );
  }

  // The split function takes three parameters:
  //
  // * A function that's called for each operation to execute
  // * The Link to use for an operation if the function returns a "truthy" value
  // * The Link to use for an operation if the function returns a "falsy" value
  // Above `authLink` so it can read the token that link stamped on the operation context, and
  // below the error link so a suppressed 401 is still annotated for `AccountProvider` — the other
  // sign-out authority, which is not gated by `onUnauthorized` and is the only one in the
  // brokerage app.
  httpLinks.push(createAuthTokenAnnotationLink(), timeoutLink, cleanTypeName, invalidationLink, authLink, apiHttpLink);
  const httpLinkChain = ApolloLink.from(httpLinks);

  const splitLink = split(
    ({ query }) => {
      const definition = getMainDefinition(query);
      return definition.kind === "OperationDefinition" && definition.operation === "subscription";
    },
    wsLink,
    httpLinkChain
  );

  return new ApolloClient({
    link: splitLink,
    queryDeduplication: true,
    cache: new InMemoryCache({
      ...cacheConfig,
    }),
    defaultOptions: {
      query: {
        // Default to not using the cache.
        // We can slowly enable this once we have a good setup for invalidating the cache.
        fetchPolicy: "network-only",
      },
      watchQuery: {
        // Apollo Client executes the full query against both the cache and your GraphQL server. The query automatically updates if the result of the server-side query modifies cached fields.
        // Provides a fast response while also helping to keep cached data consistent with server data.
        fetchPolicy: "cache-and-network",
      },
    },
  });
}
